import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import ExitButton from 'components/button/ExitButton';
import YesNoButton from 'components/button/YesNoButton';
import comptroller from 'assets/images/comptroller_white.svg';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import InputLabel from 'components/InputLabel/index';
import { Col, DatePicker, Form, Row } from 'antd';
import dayjs from 'dayjs';

const SubmitModalWithDate = ({ icon, showModal, setShowModal, setSubmitForm = () => {}, modalData, handleSubmit = () => {} }) => {
	const form = Form.useFormInstance();
	const theme = useTheme();

	// Add state to track the selected date
	const [selectedDate, setSelectedDate] = React.useState(null);
	// Add state for validation error
	const [dateError, setDateError] = React.useState(false);
	// Add state for error message
	const [errorMessage, setErrorMessage] = React.useState('Please select an effective date');
	// Add state for min date
	const [minDate, setMinDate] = React.useState(dayjs());

	const label = modalData?.label;
	let action = modalData?.activationStatus?.toLowerCase().replace(/_/g, ' ') ?? modalData?.action?.toLowerCase().replace(/_/g, ' ');

	// Calculate minimum allowed date from second-to-last change's effective date
	const calculateMinDate = React.useCallback(() => {
		const changes = form.getFieldValue('changes');

		// Check if we have at least 2 changes
		if (changes && changes.length >= 2) {
			const secondLastIndex = changes.length - 2;
			const secondLastDate = changes[secondLastIndex].inputChangeEffectiveDate;

			if (secondLastDate) {
				// Return the day after second last date as minimum
				return dayjs(secondLastDate).add(1, 'day');
			}
		}

		// If no second-to-last date, just return today
		return dayjs();
	}, [form]);

	// Update min date when modal opens
	React.useEffect(() => {
		if (showModal) {
			const calculatedMinDate = calculateMinDate();
			setMinDate(calculatedMinDate);
			// Reset date selection and errors when modal opens
			setSelectedDate(null);
			setDateError(false);
		}
	}, [showModal, calculateMinDate]);

	const validateDate = (date) => {
		if (!date) {
			setErrorMessage('Please select an effective date');
			return false;
		}

		if (date.isBefore(minDate, 'day')) {
			setErrorMessage(`Date must be after ${minDate.format('YYYY-MM-DD')}`);
			return false;
		}

		return true;
	};

	const handleOk = () => {
		if (!validateDate(selectedDate)) {
			setDateError(true);
			return;
		}

		setSubmitForm(true);
		setShowModal(false);
		// Pass the selected date to handleSubmit
		handleSubmit(selectedDate);
	};

	const handleClose = () => {
		setShowModal(false);
	};

	// Handle date change
	const handleDateChange = (date) => {
		setSelectedDate(date);
		if (validateDate(date)) {
			setDateError(false);
		} else {
			setDateError(true);
		}
	};

	return (
		<React.Fragment>
			<Dialog
				fullWidth={true}
				maxWidth={'sm'}
				open={showModal}
				keepMounted
				onClose={handleClose}
				aria-labelledby="draggable-dialog-title"
				sx={{ '& .MuiPaper-root': { borderRadius: '8px' } }}
			>
				<Box>
					<Grid
						container
						spacing={2}
						justifyContent="space-between"
						alignItems="center"
						sx={{ bgcolor: '#214689', borderBottom: `1px solid ${theme.palette.divider}` }}
					>
						<Grid item>
							<DialogTitle sx={{ color: 'common.white', display: 'flex', alignItems: 'center' }}>
								<img alt="in progress" src={icon ? icon : comptroller} width="18px" height="18px" style={{ marginRight: '8px' }} />
								{modalData?.head}
							</DialogTitle>
						</Grid>
						<Grid item sx={{ mr: 3 }}>
							<ExitButton handleClose={handleClose} />
						</Grid>
					</Grid>
					<DialogContent
						sx={{ pt: { xs: 5.25, md: 10 }, pb: { xs: 4, md: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
					>
						<Typography variant="h6" align="center" sx={{ mb: 2 }}>
							{label ? label : `Do you want to ${action}?`}
						</Typography>
						<Box sx={{ width: '100%' }}>
							<Row>
								<Col xs={8}>
									<InputLabel label="Effective Date" head={true} required={true} />
								</Col>
								<Col xs={16}>
									<DatePicker
										style={{ width: '100%', borderColor: dateError ? 'red' : undefined }}
										format="YYYY-MM-DD"
										disabledDate={(current) => {
											// Disable dates before the minimum date
											return current && current.isBefore(minDate, 'day');
										}}
										onChange={handleDateChange}
										value={selectedDate}
										status={dateError ? 'error' : ''}
									/>
									{dateError && (
										<Typography variant="caption" color="error">
											{errorMessage}
										</Typography>
									)}
								</Col>
							</Row>
						</Box>
					</DialogContent>
					<DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'center' }}>
						<YesNoButton handleClick={handleOk} bgColor="#00914f" title="Yes" />
						<YesNoButton handleClick={handleClose} bgColor="#B00020" title="No" />
					</DialogActions>
				</Box>
			</Dialog>
		</React.Fragment>
	);
};

export default SubmitModalWithDate;
